class ProductCardSwatches extends HTMLElement {
  constructor() {
    super();
    
    this.productCard = this.closest('.product-card');
    this.imageWrapper = this.productCard?.querySelector('.product-card__image-wrapper');
    this.mainImage = this.imageWrapper?.querySelector('.product-card__image--main img');
    this.secondImage = this.imageWrapper?.querySelector('.product-card__image--second img');
    
    this.originalMainSrc = this.mainImage?.src;
    this.originalMainSrcset = this.mainImage?.srcset;
    this.originalSecondSrc = this.secondImage?.src;
    this.originalSecondSrcset = this.secondImage?.srcset;
    
    // Updated selector to target the li elements with links
    this.swatchItems = this.querySelectorAll('li[data-tooltip]');
    this.swatchLinks = this.querySelectorAll('li[data-tooltip] a[href]');
    this.preloadedImages = new Map();
    
    this.init();
  }
  
  init() {

    if (!this.productCard || !this.mainImage || this.swatchLinks.length === 0) {
      return;
    }
    
    // Preload images on intersection (when card comes into view)
    this.setupIntersectionObserver();
    
    // Add hover listeners to swatch items (li elements)
    this.swatchItems.forEach(swatchItem => {
      const link = swatchItem.querySelector('a[href]');
      
      if (link) {
        const productUrl = link.getAttribute('href');
        const productHandle = productUrl.split('/').pop().split('?')[0];
        
        swatchItem.addEventListener('mouseenter', () => {
          console.log('Swatch hover:', productHandle);
          this.handleSwatchHover(productHandle);
        });
        
        swatchItem.addEventListener('mouseleave', () => {
          this.restoreOriginalImages();
        });
      }
    });
    
    // Add hover listener to the product card to restore on mouse leave
    this.productCard.addEventListener('mouseleave', () => {
      this.restoreOriginalImages();
    });
  }
  
  setupIntersectionObserver() {
    if (!window.IntersectionObserver) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.preloadSwatchImages();
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '100px' });
    
    observer.observe(this.productCard);
  }
  
  async preloadSwatchImages() {
    // Preload first few swatch images
    const swatchesToPreload = Array.from(this.swatchLinks).slice(0, 3);
    
    swatchesToPreload.forEach(async link => {
      const productUrl = link.getAttribute('href');
      const productHandle = productUrl.split('/').pop().split('?')[0];
      
      try {
        const productData = await this.loadProductData(productHandle);
        if (productData?.featured_image) {
          this.preloadImage(productData.featured_image);
        }
      } catch (error) {
        // Silently fail preloading
      }
    });
  }
  
  preloadImage(imageSrc) {
    if (this.preloadedImages.has(imageSrc)) return;
    
    const img = new Image();
    img.src = imageSrc;
    this.preloadedImages.set(imageSrc, img);
  }
  
  async handleSwatchHover(productHandle) {
    try {
      this.productCard.classList.add('product-card--loading-swatch');
      
      const productData = await this.loadProductData(productHandle);
      if (productData?.featured_image) {
        this.updateImages(productData);
      }
    } catch (error) {
      console.warn('Failed to load product image:', error);
    } finally {
      this.productCard.classList.remove('product-card--loading-swatch');
    }
  }
  
  async loadProductData(productHandle) {
    const cacheKey = `product-${productHandle}`;
    let productData = this.getFromCache(cacheKey);
    
    if (!productData) {
      console.log('Fetching product data for:', productHandle);
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) throw new Error('Product not found');
      
      productData = await response.json();
      this.setCache(cacheKey, productData, 5);
    }
    
    return productData;
  }
  
  updateImages(productData) {
    if (!this.mainImage) return;
    
    const featuredImage = productData.featured_image;
    const secondProductImage = productData.images && productData.images.length > 1 ? productData.images[1] : null;
    
    // Add transition class
    this.imageWrapper.classList.add('product-card__image--swapping');
    
    setTimeout(() => {
      // Update main image
      if (featuredImage) {
        this.mainImage.src = featuredImage;
        this.mainImage.srcset = this.generateSrcset(featuredImage);
        this.mainImage.alt = productData.title || '';
      }
      
      // Update second image if available
      if (this.secondImage && secondProductImage) {
        this.secondImage.src = secondProductImage;
        this.secondImage.srcset = this.generateSrcset(secondProductImage);
      }
      
      // Remove transition class after animation
      setTimeout(() => {
        this.imageWrapper.classList.remove('product-card__image--swapping');
      }, 400);
    }, 100);
  }
  
  restoreOriginalImages() {
    if (!this.mainImage) return;
    
    this.imageWrapper.classList.add('product-card__image--swapping');
    
    setTimeout(() => {
      // Restore main image
      if (this.originalMainSrc) {
        this.mainImage.src = this.originalMainSrc;
      }
      if (this.originalMainSrcset) {
        this.mainImage.srcset = this.originalMainSrcset;
      }
      
      // Restore second image
      if (this.secondImage && this.originalSecondSrc) {
        this.secondImage.src = this.originalSecondSrc;
        if (this.originalSecondSrcset) {
          this.secondImage.srcset = this.originalSecondSrcset;
        }
      }
      
      setTimeout(() => {
        this.imageWrapper.classList.remove('product-card__image--swapping');
      }, 400);
    }, 100);
  }
  
  generateSrcset(imageUrl) {
    const widths = [70, 140, 165, 355, 450, 535, 710, 900, 1070, 1420];
    return widths
      .map(width => {
        const resizedUrl = imageUrl.replace(/(\.(jpg|jpeg|png|webp))/, `_${width}x$1`);
        return `${resizedUrl} ${width}w`;
      })
      .join(', ');
  }
  
  setCache(key, data, minutes) {
    const expiry = new Date().getTime() + minutes * 60000;
    const cacheData = { data, expiry };
    try {
      sessionStorage.setItem(key, JSON.stringify(cacheData));
    } catch (error) {
      // Handle storage quota exceeded
    }
  }
  
  getFromCache(key) {
    try {
      const cached = sessionStorage.getItem(key);
      if (!cached) return null;
      
      const { data, expiry } = JSON.parse(cached);
      if (new Date().getTime() > expiry) {
        sessionStorage.removeItem(key);
        return null;
      }
      
      return data;
    } catch {
      return null;
    }
  }
}

customElements.define('product-card-swatches', ProductCardSwatches);